import React, { useState } from 'react';
import { Editor } from '@tiptap/react';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Highlighter,
  Type,
  Download,
} from 'lucide-react';

interface EditorToolbarProps {
  editor: Editor | null;
  /** Hide alignment, colour, highlight groups to save space in sidebar mode. */
  compact?: boolean;
  /** Called when user picks an export format from the toolbar. */
  onExport?: (format: 'word' | 'pdf') => void;
}

export const EditorToolbar: React.FC<EditorToolbarProps> = ({
  editor,
  compact = false,
  onExport,
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Close popovers when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => {
      setShowColorPicker(false);
      setShowHighlightPicker(false);
      setShowExportMenu(false);
    };

    if (showColorPicker || showHighlightPicker || showExportMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showColorPicker, showHighlightPicker, showExportMenu]);

  if (!editor) {
    return null;
  }

  const colors = [
    { name: 'Black', value: '#000000' },
    { name: 'Gray', value: '#6B7280' },
    { name: 'Red', value: '#DC2626' },
    { name: 'Orange', value: '#EA580C' },
    { name: 'Yellow', value: '#CA8A04' },
    { name: 'Green', value: '#16A34A' },
    { name: 'Blue', value: '#2563EB' },
    { name: 'Purple', value: '#9333EA' },
  ];

  const highlights = [
    { name: 'None', value: '' },
    { name: 'Yellow', value: '#FEF08A' },
    { name: 'Green', value: '#BBF7D0' },
    { name: 'Blue', value: '#BFDBFE' },
    { name: 'Pink', value: '#FBCFE8' },
    { name: 'Orange', value: '#FED7AA' },
  ];

  const ToolbarButton: React.FC<{
    onClick: (e?: React.MouseEvent) => void;
    isActive?: boolean;
    disabled?: boolean;
    children: React.ReactNode;
    title: string;
  }> = ({ onClick, isActive, disabled, children, title }) => (
    <button
      onClick={(e) => onClick(e)}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded-md transition-all duration-150 ${
        isActive
          ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-500 shadow-sm'
          : 'text-medical-500 dark:text-medical-400 hover:bg-medical-100 dark:hover:bg-medical-600/50 hover:text-medical-800 dark:hover:text-medical-200'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : 'active:scale-95'}`}
    >
      {children}
    </button>
  );

  const ToolbarGroup: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex items-center gap-0.5 px-1 py-0.5 bg-medical-50 dark:bg-medical-800/50 rounded-lg">
      {children}
    </div>
  );

  const ToolbarSpacer = () => <div className="w-1" />;

  return (
    <div className="border-b border-gray-200/80 dark:border-medical-600/80 bg-white/95 dark:bg-medical-700/95 backdrop-blur-md px-3 py-2 flex flex-wrap items-center gap-0.5 sticky top-0 z-20 rounded-t-lg">
      {/* Text Formatting */}
      <ToolbarGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold (Ctrl+B)"
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic (Ctrl+I)"
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Underline (Ctrl+U)"
        >
          <Underline className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Strikethrough"
        >
          <Strikethrough className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          isActive={editor.isActive('code')}
          title="Code"
        >
          <Code className="h-4 w-4" />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarSpacer />

      {/* Headings */}
      <ToolbarGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          <Heading1 className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
        >
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarSpacer />

      {/* Lists */}
      <ToolbarGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Quote"
        >
          <Quote className="h-4 w-4" />
        </ToolbarButton>
      </ToolbarGroup>

      {!compact && (
        <>
          <ToolbarSpacer />

          {/* Text Alignment */}
          <ToolbarGroup>
            <ToolbarButton
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              isActive={editor.isActive({ textAlign: 'left' })}
              title="Align Left"
            >
              <AlignLeft className="h-4 w-4" />
            </ToolbarButton>

            <ToolbarButton
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              isActive={editor.isActive({ textAlign: 'center' })}
              title="Align Center"
            >
              <AlignCenter className="h-4 w-4" />
            </ToolbarButton>

            <ToolbarButton
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              isActive={editor.isActive({ textAlign: 'right' })}
              title="Align Right"
            >
              <AlignRight className="h-4 w-4" />
            </ToolbarButton>

            <ToolbarButton
              onClick={() => editor.chain().focus().setTextAlign('justify').run()}
              isActive={editor.isActive({ textAlign: 'justify' })}
              title="Justify"
            >
              <AlignJustify className="h-4 w-4" />
            </ToolbarButton>
          </ToolbarGroup>

          <ToolbarSpacer />
        </>
      )}

      {/* Color & Highlight */}
      {!compact && <ToolbarGroup>
        {/* Text Color */}
        <div className="relative">
          <ToolbarButton
            onClick={(e) => {
              e?.stopPropagation();
              setShowColorPicker(!showColorPicker);
              setShowHighlightPicker(false);
            }}
            title="Text Color"
          >
            <Type className="h-4 w-4" />
          </ToolbarButton>
          {showColorPicker && (
            <div
              className="absolute top-full left-0 mt-2 bg-white dark:bg-medical-700 border border-gray-200/80 dark:border-medical-600 rounded-xl shadow-xl p-3 z-20 popover-enter"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="text-xs font-medium text-medical-500 dark:text-medical-400 mb-2">Text color</p>
              <div className="grid grid-cols-4 gap-1.5">
                {colors.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => {
                      editor.chain().focus().setColor(color.value).run();
                      setShowColorPicker(false);
                    }}
                    className="w-7 h-7 rounded-md border border-gray-200 dark:border-medical-500 hover:ring-2 hover:ring-primary-400 hover:ring-offset-1 transition-all"
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
              <button
                onClick={() => {
                  editor.chain().focus().unsetColor().run();
                  setShowColorPicker(false);
                }}
                className="mt-2 w-full text-xs text-medical-500 dark:text-medical-400 hover:text-medical-800 dark:hover:text-medical-200 py-1 rounded hover:bg-medical-50 dark:hover:bg-medical-600/50 transition-colors"
              >
                Reset color
              </button>
            </div>
          )}
        </div>

        {/* Highlight */}
        <div className="relative">
          <ToolbarButton
            onClick={(e) => {
              e?.stopPropagation();
              setShowHighlightPicker(!showHighlightPicker);
              setShowColorPicker(false);
            }}
            isActive={editor.isActive('highlight')}
            title="Highlight"
          >
            <Highlighter className="h-4 w-4" />
          </ToolbarButton>
          {showHighlightPicker && (
            <div
              className="absolute top-full left-0 mt-2 bg-white dark:bg-medical-700 border border-gray-200/80 dark:border-medical-600 rounded-xl shadow-xl p-3 z-20 popover-enter"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="text-xs font-medium text-medical-500 dark:text-medical-400 mb-2">Highlight</p>
              <div className="grid grid-cols-3 gap-1.5">
                {highlights.map((highlight) => (
                  <button
                    key={highlight.name}
                    onClick={() => {
                      if (highlight.value) {
                        editor.chain().focus().setHighlight({ color: highlight.value }).run();
                      } else {
                        editor.chain().focus().unsetHighlight().run();
                      }
                      setShowHighlightPicker(false);
                    }}
                    className="w-7 h-7 rounded-md border border-gray-200 dark:border-medical-500 hover:ring-2 hover:ring-primary-400 hover:ring-offset-1 transition-all"
                    style={{ backgroundColor: highlight.value || (document.documentElement.classList.contains('dark') ? '#374151' : '#ffffff') }}
                    title={highlight.name}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </ToolbarGroup>}

      <ToolbarSpacer />

      {/* Undo/Redo */}
      <ToolbarGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo (Ctrl+Z)"
        >
          <Undo className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo (Ctrl+Y)"
        >
          <Redo className="h-4 w-4" />
        </ToolbarButton>
      </ToolbarGroup>

      {/* Export */}
      {onExport && (
        <>
          <div className="flex-1" />
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowExportMenu(!showExportMenu);
                setShowColorPicker(false);
                setShowHighlightPicker(false);
              }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary-50/60 hover:bg-primary-100 dark:bg-primary-900/10 dark:hover:bg-primary-900/30 border border-primary-200 dark:border-primary-700 text-primary-600 dark:text-primary-400 text-xs font-medium transition-colors"
              title="Export report"
            >
              <Download className="h-3.5 w-3.5" />
              Export
            </button>
            {showExportMenu && (
              <div
                className="absolute right-0 top-full mt-1 bg-white dark:bg-medical-800 border border-medical-200 dark:border-medical-600 rounded-lg shadow-lg z-50 py-1 min-w-[160px]"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => { onExport('word'); setShowExportMenu(false); }}
                  className="w-full text-left px-3 py-2 text-xs text-medical-700 dark:text-medical-300 hover:bg-medical-50 dark:hover:bg-medical-700 flex items-center gap-2"
                >
                  <span className="w-5 text-center text-[10px] font-semibold text-cyan-600 dark:text-cyan-400">W</span>
                  Word Document (.docx)
                </button>
                <button
                  onClick={() => { onExport('pdf'); setShowExportMenu(false); }}
                  className="w-full text-left px-3 py-2 text-xs text-medical-700 dark:text-medical-300 hover:bg-medical-50 dark:hover:bg-medical-700 flex items-center gap-2"
                >
                  <span className="w-5 text-center text-[10px] font-semibold text-red-600 dark:text-red-400">PDF</span>
                  PDF Document (.pdf)
                </button>
              </div>
            )}
          </div>
        </>
      )}

    </div>
  );
};
